import { get as getCookie, remove as removeCookie, set as setCookie } from 'js/lib/cookie';
import { isAuthenticatedUser } from 'js/lib/user';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import EpicClient from 'bundles/epic/clients/__generated__/siteMisc';

import { doesAppSupportDarkMode } from './doesAppSupportDarkMode';
import type { ColorSchemePreference, ColorSchemeRenderedOnTheClient } from './types';

export const COLOR_THEME_COOKIE = 'colorTheme';
export const DARK_MODE_BANNER_DISMISSED_STORAGE_KEY = 'dark_mode_banner_dismissed';

const getValidColorTheme = (input: string | null | undefined): ColorSchemePreference | undefined => {
  switch (input) {
    case 'light':
      return 'light';
    case 'dark':
      return 'dark';
    case 'system':
      return 'system';
    default:
      return undefined;
  }
};

export const getThemeFromUrl = (): ColorSchemePreference | undefined => {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const themeFromUrl = params.get('theme') ?? params.get('colorTheme') ?? undefined;
  return getValidColorTheme(themeFromUrl);
};

/**
 * Detects whether the browser supports dark mode.
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/color_value/light-dark
 * @returns {boolean} true if light-dark() is supported, false otherwise
 */
export const supportsDarkMode = (): boolean => {
  try {
    if (typeof CSS === 'undefined' || !CSS.supports) {
      return false;
    }
    return CSS.supports('color', 'light-dark(red, blue)');
  } catch {
    return false;
  }
};

export const prefersDarkMode = (): boolean => {
  return typeof window !== 'undefined' && window?.matchMedia('(prefers-color-scheme: dark)')?.matches;
};

/**
 * Checks if user meets all technical requirements for the dark mode experiment.
 * Does NOT check experiment variant - only eligibility criteria.
 *
 * @returns true if user is authenticated, supports dark mode, and has device in dark mode
 */
const isEligibleForDarkModeExperiment = (): boolean => {
  return isAuthenticatedUser() && supportsDarkMode() && prefersDarkMode();
};

export const getColorSchemePreference = () => {
  // ignore the cookie for unauthenticated users
  // to reduce the scope of the dark mode project
  if (!isAuthenticatedUser()) return undefined;
  // ignore the cookie for apps that don't support dark mode
  // (internal or partner facing apps)
  // to reduce the scope of the dark mode project
  if (!doesAppSupportDarkMode()) return undefined;

  return getValidColorTheme(getCookie(COLOR_THEME_COOKIE));
};

export const setColorSchemePreference = (preference: ColorSchemePreference) => {
  // explicitly set the cookie to expire in 400 days, chrome max
  setCookie(COLOR_THEME_COOKIE, preference, { days: 400 });
};

export const clearColorSchemePreference = () => {
  removeCookie(COLOR_THEME_COOKIE);
};

export const wasDarkModeBannerDismissedInLocalStorage = (): boolean => {
  return localStorageEx.getItem(DARK_MODE_BANNER_DISMISSED_STORAGE_KEY, JSON.parse, false);
};

export const setDarkModeBannerDismissedInLocalStorage = () => {
  localStorageEx.setItem(DARK_MODE_BANNER_DISMISSED_STORAGE_KEY, true, JSON.stringify);
};

export const clearDarkModeBannerDismissedInLocalStorage = () => {
  localStorageEx.removeItem(DARK_MODE_BANNER_DISMISSED_STORAGE_KEY);
};

const checkExperimentEpic = () => EpicClient.get('enableDarkModeExperiment2') === 'experimental';
const checkDarkModeGA = () => EpicClient.get('darkModeGA');

export const enableAppearancePreferences = (): boolean => {
  const enableForUsersWhoAlreadyHaveTheCookie = Boolean(getCookie(COLOR_THEME_COOKIE));
  const enableWithThemeFromUrl = Boolean(getThemeFromUrl());
  const enableFromExperiment = checkExperimentEpic();
  const enableFromGA = checkDarkModeGA();
  return enableForUsersWhoAlreadyHaveTheCookie || enableWithThemeFromUrl || enableFromExperiment || enableFromGA;
};

/**
 * Determines if the dark mode banner should be shown to the user.
 * Despite the name (kept for backwards compatibility), this is now used for GA rollout.
 *
 * The banner requires the user's system preference to be dark mode — both for the
 * original experiment and the GA rollout — so users are only prompted when it's relevant.
 * The appearance settings section (controlled separately by enableAppearancePreferences)
 * is available to all authenticated users in GA regardless of system preference.
 *
 * @returns true if the dark mode banner should be shown
 */
export const enableDarkModeExperiment = (): boolean => {
  const enableForUsersWhoAlreadyHaveTheCookie = Boolean(getCookie(COLOR_THEME_COOKIE));
  const enableWithThemeFromUrl = Boolean(getThemeFromUrl());

  let enableFromExperiment = false;
  if (isEligibleForDarkModeExperiment()) {
    enableFromExperiment = checkExperimentEpic();
  }

  // GA rollout: same eligibility as the experiment — requires system dark mode preference
  const enableFromGA = (isEligibleForDarkModeExperiment() && checkDarkModeGA()) ?? false;

  return enableForUsersWhoAlreadyHaveTheCookie || enableWithThemeFromUrl || enableFromExperiment || enableFromGA;
};

export const getInitialColorTheme = () => {
  const themeFromUrl = getThemeFromUrl();
  return themeFromUrl ?? getColorSchemePreference() ?? 'light';
};

export const getColorSchemeRenderedOnTheClient = (
  preference: ColorSchemePreference
): ColorSchemeRenderedOnTheClient => {
  switch (preference) {
    case 'dark':
      return 'dark';
    case 'light':
      return 'light';
    case 'system':
      if (typeof window === 'undefined') return 'light';
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    default:
      return 'light';
  }
};

export const parseLightDarkValue = (value: string) => {
  // This regex matches 'light-dark(<anything>, <anything>)', handling nested commas (e.g. in rgb(a))
  // It uses a balanced approach to match commas not inside parentheses.
  const lightDarkPrefix = 'light-dark(';
  if (!value.startsWith(lightDarkPrefix) || !value.endsWith(')')) return [undefined, undefined];

  const content = value.slice(lightDarkPrefix.length, -1).trim();

  let depth = 0;
  let splitIndex = -1;
  for (let i = 0; i < content.length; i += 1) {
    const char = content[i];
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth -= 1;
    } else if (char === ',' && depth === 0) {
      splitIndex = i;
      break;
    }
  }
  if (splitIndex === -1) return [undefined, undefined];

  const lightValue = content.slice(0, splitIndex).trim();
  const darkValue = content.slice(splitIndex + 1).trim();

  return [lightValue, darkValue] as const;
};

const getComputedPropertyValue = (propertyName: string) => {
  return getComputedStyle(document.documentElement).getPropertyValue(propertyName);
};

export const getColorCodeFromCssVariable = (token: string, theme: ColorSchemeRenderedOnTheClient): string => {
  const propertyName = token.startsWith('var(') ? token.slice('var('.length, -1) : token;
  const value = getComputedPropertyValue(propertyName).trim();
  if (value.startsWith('light-dark(')) {
    const [lightValue, darkValue] = parseLightDarkValue(value);
    const themeValue = theme === 'dark' ? darkValue : lightValue;
    if (themeValue?.startsWith('var(')) {
      return getColorCodeFromCssVariable(themeValue, theme);
    }
    return themeValue?.trim() ?? '';
  }
  if (value.startsWith('var(')) {
    return getColorCodeFromCssVariable(value, theme);
  }
  return value;
};
